'use client';

import { useState } from 'react';

import type { EventDetail } from '@eliadmin/core/src/api/events/events-api';
import type { Level, Room } from '@eliadmin/core/src/api/courses/coursers-api';
import type { ImageType } from '@eliadmin/core/src/api/api-types';
import type { TranslateFn } from '@eliadmin/core/src/hooks/use-translations';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { HiOutlineLocationMarker, HiOutlineCalendar } from 'react-icons/hi';

import { HtmlDescription } from '../../shared/html-description';
import { EventCourses } from './event-courses';
import { EventServices } from './event-services';

export const getDayTranslationString = (
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  t: TranslateFn,
) => {
  switch (day) {
    case 'monday':
      return t('text1302');
    case 'tuesday':
      return t('text1303');
    case 'wednesday':
      return t('text1304');
    case 'thursday':
      return t('text1305');
    case 'friday':
      return t('text1306');
    case 'saturday':
      return t('text1307');
    case 'sunday':
      return t('text1308');
  }
};

export const translateCustomDate = (date: string, t: TranslateFn) => {
  return date
    .replace(/\bmonday\b/gi, getDayTranslationString('monday', t))
    .replace(/\btuesday\b/gi, getDayTranslationString('tuesday', t))
    .replace(/\bwednesday\b/gi, getDayTranslationString('wednesday', t))
    .replace(/\bthursday\b/gi, getDayTranslationString('thursday', t))
    .replace(/\bfriday\b/gi, getDayTranslationString('friday', t))
    .replace(/\bsaturday\b/gi, getDayTranslationString('saturday', t))
    .replace(/\bsunday\b/gi, getDayTranslationString('sunday', t));
};

const STUDIO_CONTENT: Record<string, string> = {
  sk: `
<p>Danceana štúdio ponúka priestor na prenájom pre tanečné tréningy, workshopy, lekcie aj pohybové aktivity.</p>
<p>Štúdio má rozlohu <strong>146 m²</strong>, z toho tanečná sála <strong>10 × 10 m</strong> a doplnkové priestory <strong>46 m²</strong>.</p>
<p><strong>Vybavenie štúdia:</strong></p>
<ul>
  <li>Pružná a protišmyková tanečná podlaha</li>
  <li>Zrkadlá v sále (s možnosťou zakrytia závesom)</li>
  <li>Zvuková technika a špeciálne osvetlenie</li>
  <li>Šatne so sprchami (×2) a toalety (×2)</li>
  <li>Klimatizované a vzduchotechnicky riešené priestory</li>
  <li>WiFi pripojenie</li>
  <li>Čisté a príjemné prostredie</li>
</ul>
<br/>
<p><strong>Cena:</strong> 1 hodina – 20 €</p>
<p><strong>Kontakt:</strong> <a href="mailto:info@danceana.sk">info@danceana.sk</a> alebo <a href="tel:+421908593235">0908 593 235</a></p>
  `.trim(),
  en: `
<p>Danceana studio offers space for rent for dance training, workshops, lessons, and movement activities.</p>
<p>The studio has a total area of <strong>146 m²</strong>, including a dance hall of <strong>10 × 10 m</strong> and additional spaces of <strong>46 m²</strong>.</p>
<p><strong>Studio equipment:</strong></p>
<ul>
  <li>Flexible and non-slip dance floor</li>
  <li>Mirrors in the hall (with option to cover with curtains)</li>
  <li>Sound system and special lighting</li>
  <li>Changing rooms with showers (×2) and toilets (×2)</li>
  <li>Air-conditioned and ventilated spaces</li>
  <li>WiFi connection</li>
  <li>Clean and pleasant environment</li>
</ul>
<br/>
<p><strong>Price:</strong> 1 hour – 20 €</p>
<p><strong>Contact:</strong> <a href="mailto:info@danceana.sk">info@danceana.sk</a> or <a href="tel:+421908593235">0908 593 235</a></p>
  `.trim(),
};

const TABS = ['services', 'other_description', 'description', 'reservation', 'studio'] as const;
type Tab = (typeof TABS)[number];

const STUDIO_EVENT_SLUG = 'danceana-studio-nobelova';

// Štítky záložiek špecifické pre detail štúdia – ostatné eventy používajú preklady z administrácie.
const STUDIO_TAB_LABELS: Record<string, Partial<Record<Tab, string>>> = {
  sk: { services: 'O štúdiu', other_description: 'Cenník', reservation: 'Calendiari' },
  en: { services: 'About the studio', other_description: 'Pricing', reservation: 'Calendiari' },
};

interface EventHeaderProps {
  event: EventDetail;
  locale: string;
  rooms: Room[];
  levels: Level[];
  schedule: ImageType;
}

export function EventHeader({ event, locale, rooms, levels, schedule }: EventHeaderProps) {
  const { t } = useTranslations();

  const isStudioEvent = event.slug === STUDIO_EVENT_SLUG;
  const studioTabLabels = isStudioEvent
    ? (STUDIO_TAB_LABELS[locale] ?? STUDIO_TAB_LABELS.sk)
    : undefined;

  const TAB_LABELS: Record<Tab, string> = {
    services: studioTabLabels?.services ?? t('text1000'),
    other_description: studioTabLabels?.other_description ?? t('text3824'),
    description: t('text274'),
    reservation: studioTabLabels?.reservation ?? t('text261'),
    studio: t('text3787'),
  };

  // Registrácia je vždy len externý odkaz – bez URL v text_fields sa tab nezobrazí.
  const externalReservationURL = event.text_fields?.find(f => f.id === 3)?.value;

  const tabAvailability: Record<Tab, boolean> = {
    services: !!event.text_fields?.find(f => f.id === 5)?.value,
    other_description: !!event.text_fields?.find(f => f.id === 6)?.value,
    description: !isStudioEvent && !!event.description,
    reservation: !!externalReservationURL,
    studio: isStudioEvent && !!(STUDIO_CONTENT[locale] ?? STUDIO_CONTENT.sk),
  };

  // Ikonka lokality sa zobrazí len vtedy, keď je z čoho poskladať adresu –
  // prázdne alebo medzerové hodnoty z API sa nepočítajú.
  const streetLine = [event.address, event.street_number]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
  const location = [event.location_name?.trim(), streetLine, event.city?.trim()]
    .filter(Boolean)
    .join(', ');

  const availableTabs = TABS.filter(tab => tabAvailability[tab]);
  // Registrácia nemá vlastný obsah, len odkazuje von – nesmie sa stať aktívnym tabom.
  const contentTabs = availableTabs.filter(tab => tab !== 'reservation');
  const [activeTab, setActiveTab] = useState<Tab>(() => contentTabs[0] ?? 'services');

  return (
    <div className="flex w-full flex-col items-start justify-start gap-6">
      <h1 className="font-heading text-4xl font-medium text-black sm:text-5xl lg:text-6xl">
        {event.name}
      </h1>

      <div className="flex w-full flex-wrap items-start justify-start gap-x-6 gap-y-3">
        {location && (
          <div className="flex items-center justify-start gap-1.5 sm:gap-2">
            <HiOutlineLocationMarker size={24} className="text-primary" />
            <p className="text-sm font-normal sm:text-base">{location}</p>
          </div>
        )}

        {isStudioEvent && (
          <div className="flex items-center justify-start gap-1.5 sm:gap-2">
            <HiOutlineCalendar size={24} className="text-primary" />
            <p className="text-sm font-normal sm:text-base">
              {getDayTranslationString('monday', t)} – {getDayTranslationString('sunday', t)}
            </p>
          </div>
        )}

        {!isStudioEvent && event.date_start && (
          <div className="flex items-center justify-start gap-1.5 sm:gap-2">
            <HiOutlineCalendar size={24} className="text-primary" />
            <p className="text-sm font-normal sm:text-base">
              {new Date(event.date_start).toLocaleDateString(locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
              {event.date_stop && event.date_stop !== event.date_start && (
                <>
                  {' – '}
                  {new Date(event.date_stop).toLocaleDateString(locale, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </>
              )}
            </p>
          </div>
        )}
      </div>

      <div className="flex w-full items-end gap-0 overflow-x-auto border-b border-black/10">
        {availableTabs.map(tab => {
          const tabClass = cn(
            'ea-event-header__tab',
            'shrink-0 whitespace-nowrap px-4 py-2.5 text-sm font-semibold transition-colors sm:px-6 sm:text-base',
            activeTab === tab
              ? 'border-b-2 border-primary text-primary'
              : 'text-black/50 hover:text-black/80',
          );
          if (tab === 'reservation' && externalReservationURL) {
            return (
              <a
                key={tab}
                href={externalReservationURL}
                target="_blank"
                rel="noopener noreferrer"
                className={tabClass}
              >
                {TAB_LABELS[tab]}
              </a>
            );
          }
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={tabClass}
            >
              {TAB_LABELS[tab]}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'services' && <EventServices event={event} />}

      {activeTab === 'other_description' && (
        <HtmlDescription
          content={event.text_fields?.find(f => f.id === 6)?.value ?? ''}
          className={cn(
            'w-full bg-[#F6F6F6] p-6 text-black sm:p-8',
            'ea-course-event__description-content',
          )}
        />
      )}

      {activeTab === 'description' && event.description && (
        <HtmlDescription
          content={event.description}
          className={cn(
            'w-full bg-[#F6F6F6] p-6 text-black sm:p-8',
            'ea-course-event__description-content',
          )}
        />
      )}

      {activeTab === 'studio' && (
        <HtmlDescription
          content={STUDIO_CONTENT[locale] ?? STUDIO_CONTENT.sk}
          className={cn(
            'w-full bg-[#F6F6F6] p-6 text-black sm:p-8',
            'ea-course-event__description-content',
          )}
        />
      )}

      {/* Výpis kurzov je samostatná sekcia pod popisom, nie obsah tabu. */}
      {!!event.lecturers?.length && (
        <EventCourses
          event={event}
          locale={locale}
          lecturers={event.lecturers}
          levels={levels}
          rooms={rooms}
          schedule={schedule}
        />
      )}
    </div>
  );
}
